import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { mocApi } from '../api/client';
import { permRoleFromApiRoles } from '../lib/permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await fetch(`${API}/auth/me`, { credentials: 'include' });
      if (!res.ok) throw new Error('unauth');
      const u = await res.json();
      try {
        const roles = await mocApi.getUserRoles();
        if (Array.isArray(roles) && roles.length) { u.mocRoles = roles; u.role = permRoleFromApiRoles(roles); }
      } catch { /* keep backend default role */ }
      if (!u.role) u.role = 'pilote';
      setUser(u);
      // Préchargement en arrière-plan : réchauffe le cache du proxy (45 s) juste après la
      // connexion pour que la 1re navigation (Mes MOC, Nouvelle MOC, Notifications) soit instantanée.
      Promise.allSettled([
        mocApi.getDashboard(),
        mocApi.listMocs(),
        mocApi.listValidations(),
        mocApi.listNotifications(),
        mocApi.listSites(),
      ]);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: skip /me while an OAuth callback is being processed.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    await fetch(`${API}/auth/logout`, { method: 'POST', credentials: 'include' });
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, checkAuth, logout, API }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
